// @ts-check
import SmrClient from "Utils/Requests/SmrClient";

class UsersRequests {
    /**
     * @param {number} start
     * @param {number} length
     * @param {string} userName
     * @param {boolean} updateFromTimer
     * @param {boolean} skipError
     * @returns {Promise<GetUsersResponse>}
     */
    async GetUsers(start, length, userName, updateFromTimer, skipError) {
        return await SmrClient.FetchJson({
            url: 'Users/GetUsers',
            method: "POST",
            body: { start: start, length: length, userName: userName, updateFromTimer: updateFromTimer },
            bodyIsUrlParams: true,
            skipError: skipError
        });
    }

    /**
     * @returns {Promise<GetBulkSyncSettingsResponse>}
     */
    async GetBulkSyncSettings() {
        return await SmrClient.FetchJson({
            url: 'Users/GetBulkSyncSettings',
            method: "GET"
        });
    }

    /**
     * @returns {Promise<UsersInitDataResponse>}
     */
    async GetInitData() {
        return await SmrClient.FetchJson({
            url: 'Users/GetInitData',
            method: "GET"
        });
    }

    /**
     * @param {string} code
     * @returns {Promise<GetDeepLinkResponse>}
     */
    async GetLoginDeepLink(code) {
        return await SmrClient.FetchJson({
            url: 'Users/GetLoginDeepLink',
            method: "POST",
            body: { code: code },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {string} code
     * @returns {Promise<VoidResponse>}
     */
    async DeleteDevice(code) {
        return await SmrClient.FetchJson({
            url: 'Users/DeleteDevice',
            method: "POST",
            body: { code: code },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {number} userId
     * @param {string} deviceCode
     * @param {string} deviceType
     * @returns {Promise<ChangeDeviceResponse>}
     */
    async AddDevice(userId, deviceCode, deviceType) {
        return await SmrClient.FetchJson({
            url: 'Users/AddDevice',
            method: "POST",
            body: { userId: userId, deviceCode: deviceCode, deviceType: deviceType },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {string} backendFuncName 
     * @param {string} deviceCode
     * @param {string | boolean | number | null} newVal 
     * @returns {Promise<ChangeDeviceResponse>}
     */
    async ChangeDeviceSetting(backendFuncName, deviceCode, newVal) {
        return await SmrClient.FetchJson({
            url: `Users/${backendFuncName}`,
            method: "POST",
            body: { deviceCode: deviceCode, newVal: newVal },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {number} userId
     * @returns {Promise<DeleteUserResponse>}
     */
    async DeleteUser(userId) {
        return await SmrClient.FetchJson({
            url: 'Users/DeleteUser',
            method: "POST",
            body: { userId: userId },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {number} userId
     * @returns {Promise<DeleteUserResponse>}
     */
    async DeleteUserPhysically(userId) {
        return await SmrClient.FetchJson({
            url: 'Users/DeleteUserPhysically',
            method: "POST",
            body: { userId: userId },
            bodyIsUrlParams: true
        });
    }

    /**
     * @returns {Promise<SyncUserInfo[]>}
     */
    async GetResyncUsersResult() {
        return await SmrClient.FetchJson({
            url: 'Users/GetResyncUsersResult',
            method: "GET"
        });
    }

    async ClearResyncUsersResult() {
        return await SmrClient.FetchJson({
            url: 'Users/ClearResyncUsersResult',
            method: "GET"
        });
    }

    async StopSyncSyncedProcess() {
        return await SmrClient.FetchJson({
            url: 'Users/StopSyncSyncedProcess',
            method: "GET"
        });
    }

    /**
     * @param {string[]} usersLids 
     * @param {Date | null} receiveDocumentsFrom 
     */
    async SyncUsers(usersLids, receiveDocumentsFrom) {
        return await SmrClient.FetchJson({
            url: 'Users/SyncUsers',
            method: "POST",
            body: { usersLids: usersLids, receiveDocumentsFrom: receiveDocumentsFrom }
        });
    }

    /**
     * @param {DeviceDataDumpWeb} dumpInfo
     */
    async DownloadDeviceDbDump(dumpInfo) {
        await SmrClient.FetchDownload({
            url: 'Users/DownloadDeviceDbDump',
            method: "POST",
            body: dumpInfo
        });
    }

    /**
     * @param {DeviceDataDumpWeb} dumpInfo
     */
    async DeleteDeviceDataDump(dumpInfo) {
        return await SmrClient.FetchJson({
            url: 'Users/DeleteDeviceDataDump',
            method: "POST",
            body: dumpInfo
        });
    }

    /**
     * @param {string} deviceCode
     */
    async SendTestPushAlert(deviceCode) {
        return await SmrClient.FetchJson({
            url: 'Users/SendTestPushAlert',
            method: "POST",
            body: { deviceCode: deviceCode },
            bodyIsUrlParams: true
        });
    }

    /**
     * @param {number} deviceId 
     * @returns {Promise<ValueResponse<DeviceTraceKeysInfo>>}
     */
    async GetDeviceTraceKeys(deviceId) {
        return await SmrClient.FetchJson({
            url: 'Users/GetDeviceTraceKeys',
            method: "GET",
            body: { deviceId }
        });
    }

    /**
     * @param {DeviceTraceKeysInfo} keysInfo
     * @returns {Promise<ValueResponse<DeviceTraceKeysInfo>>}
     */
    async SetDeviceTraceKeys(keysInfo) {
        return await SmrClient.FetchJson({
            url: 'Users/SetDeviceTraceKeys',
            method: "POST",
            body: keysInfo
        });
    }

    /**
     * @param {number} deviceId 
     * @returns {Promise<ValueResponse<boolean>>}
     */
    async ClearDeviceTraces(deviceId) {
        return await SmrClient.FetchJson({
            url: 'Users/ClearDeviceTraces',
            method: "POST",
            body: { deviceId },
            bodyIsUrlParams: true
        });
    }
}

export default new UsersRequests;